import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Grid, GridItem, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {Controller, Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {calculateVerticalPadding} from '../utils'
import ProductBox from './partials/product-box'
import {useProducts} from '@salesforce/commerce-sdk-react'

export const ProductMoodboard = ({
    hasPaddings = true,
    maxWidth = remCalc(1920),
    productMoodboardGroup = [],
}) => {
    const swiperViewType = useBreakpointValue({base: 'mobile', md: 'desktop'})
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('ProductMoodboard')
    const {currency} = useCurrency()

    const [contentSwiper, setContentSwiper] = useState(null)

    var productIds = []
    productMoodboardGroup.forEach((group) =>
        group.products?.data.forEach((p) => productIds.push(p.id)),
    )

    const {data: productsData} = useProducts({
        parameters: {
            ids: productIds.join(','),
            allImages: false,
            currency,
        },
    })

    return (
        productMoodboardGroup?.length > 0 && (
            <Box
                data-testid="product-moodboard"
                my={verticalPadding}
                maxW={maxWidth}
                {...styles.container}
            >
                <Box __css={{...swiperStyles, ...styles.tabs}}>
                    <Swiper
                        key={'mobile-view'}
                        modules={[Controller]}
                        slidesPerView={3}
                        enabled={false}
                        centeredSlides={true}
                        onSwiper={setContentSwiper}
                    >
                        {productMoodboardGroup.map((tile, tIndex) => (
                            <SwiperSlide
                                key={`navigation-slider-tile-${tIndex}`}
                                modules={[Controller]}
                            >
                                {({isActive}) => (
                                    <Text
                                        textStyle={'body-small'}
                                        fontWeight={'bold'}
                                        color={'text.default'}
                                        py={remCalc(24)}
                                        textTransform={isActive ? 'uppercase' : 'none'}
                                        textDecoration={isActive ? 'underline' : 'none'}
                                        textUnderlineOffset={remCalc(4)}
                                    >
                                        {tile.label || ''}
                                    </Text>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
                <Box __css={{...swiperStyles, ...styles.content}}>
                    <Swiper
                        key={{base: 'mobile-view', md: 'other-view'}}
                        modules={[Controller, Navigation, Pagination, Keyboard]}
                        controller={{control: contentSwiper}}
                        pagination={{
                            clickable: true,
                            el: '.product-moodboard-pagination',
                            renderBullet: (index, className) =>
                                `<span class="${className}">${productMoodboardGroup[index].label}</span>`,
                        }}
                        className={`product-moodboard-swiper ${swiperViewType}`}
                    >
                        <Flex
                            className="product-moodboard-pagination"
                            {...styles.pagination}
                            textStyle={'body-small'}
                            fontWeight={'bold'}
                        />
                        {productMoodboardGroup.map((tile, tIndex) => {
                            const hasTwoTiles = tile.products?.data?.length == 2
                            const hasThreeTiles = tile.products?.data?.length == 3
                            const hasFiveTiles = tile.products?.data?.length == 5
                            const hasMoreThanTwoTiles = tile.products?.data?.length > 2
                            const hasMoreThanThreeTiles = tile.products?.data?.length > 3
                            const hasLessThanFiveTiles = tile.products?.data?.length < 5
                            return (
                                <SwiperSlide key={`product-moodboard-tile-${tIndex}`}>
                                    <Box>
                                        <Grid
                                            width={'100%'}
                                            h={{
                                                base: hasFiveTiles ? remCalc(964) : remCalc(840),
                                                sm: hasFiveTiles ? remCalc(944) : remCalc(850),
                                                md: remCalc(488),
                                                lg: remCalc(622),
                                            }}
                                            gap={{base: remCalc(16), md: remCalc(24)}}
                                            templateRows={{
                                                base: 'repeat(4, 1fr)',
                                                md: 'repeat(2, 1fr)',
                                            }}
                                            templateColumns={{
                                                base: 'repeat(2, 1fr)',
                                                md: 'repeat(4, 1fr)',
                                            }}
                                        >
                                            <GridItem
                                                rowSpan={2}
                                                colSpan={2}
                                                {...styles.productbox}
                                            >
                                                {tile.products?.data[0] && (
                                                    <ProductBox
                                                        product={{
                                                            ...productsData?.data?.find(
                                                                (product) =>
                                                                    product.id ==
                                                                    tile.products?.data[0].id,
                                                            ),
                                                        }}
                                                        imageClass={'image-big'}
                                                    />
                                                )}
                                            </GridItem>
                                            <GridItem
                                                {...styles.productbox}
                                                rowSpan={hasTwoTiles ? 2 : 1}
                                                colSpan={hasLessThanFiveTiles ? 2 : 1}
                                            >
                                                {tile.products?.data[1] && (
                                                    <ProductBox
                                                        product={{
                                                            ...productsData?.data?.find(
                                                                (product) =>
                                                                    product.id ==
                                                                    tile.products?.data[1].id,
                                                            ),
                                                        }}
                                                        imageClass={
                                                            hasTwoTiles
                                                                ? 'image-big'
                                                                : hasFiveTiles
                                                                ? 'image-small'
                                                                : 'image-medium'
                                                        }
                                                        flexDirectionRow={
                                                            hasMoreThanTwoTiles &&
                                                            hasLessThanFiveTiles
                                                        }
                                                    />
                                                )}
                                            </GridItem>
                                            {hasMoreThanTwoTiles && (
                                                <GridItem
                                                    {...styles.productbox}
                                                    rowSpan={1}
                                                    colSpan={hasThreeTiles ? 2 : 1}
                                                >
                                                    {tile.products?.data[2] && (
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[2].id,
                                                                ),
                                                            }}
                                                            imageClass={
                                                                hasThreeTiles
                                                                    ? 'image-medium'
                                                                    : 'image-small'
                                                            }
                                                            flexDirectionRow={hasThreeTiles}
                                                        />
                                                    )}
                                                </GridItem>
                                            )}
                                            {hasMoreThanThreeTiles && (
                                                <GridItem
                                                    {...styles.productbox}
                                                    rowSpan={1}
                                                    colSpan={1}
                                                >
                                                    {tile.products?.data[3] && (
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[3].id,
                                                                ),
                                                            }}
                                                            imageClass={'image-small'}
                                                        />
                                                    )}
                                                </GridItem>
                                            )}
                                            {hasFiveTiles && (
                                                <GridItem
                                                    {...styles.productbox}
                                                    rowSpan={1}
                                                    colSpan={1}
                                                >
                                                    {tile.products?.data[4] && (
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[4].id,
                                                                ),
                                                            }}
                                                            imageClass={'image-small'}
                                                        />
                                                    )}
                                                </GridItem>
                                            )}
                                        </Grid>
                                    </Box>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </Box>
            </Box>
        )
    )
}

ProductMoodboard.propTypes = {
    hasPaddings: PropTypes.bool,
    maxWidth: PropTypes.string,
    productMoodboardGroup: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            products: PropTypes.shape({
                data: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        name: PropTypes.string,
                        image: PropTypes.shape({
                            alt: PropTypes.string,
                            link: PropTypes.string,
                            title: PropTypes.string,
                        }),
                        currency: PropTypes.string,
                        price: PropTypes.number,
                    }),
                ),
            }),
        }),
    ),
}
